<template>
  <span class="api-key-item-permissions-summary">
    <BittsTooltip placement="top">
      <span class="api-key-item-permissions-summary__piece">
        {{ scopesLabel }}
      </span>
      <template #title>
        <span class="api-key-item-permissions-summary__tooltip">
          {{ apiKey.scopes.join(', ') }}
        </span>
      </template>
    </BittsTooltip>
    <span class="api-key-item-permissions-summary__sep">·</span>
    <BittsTooltip placement="top">
      <span class="api-key-item-permissions-summary__piece">
        {{ ipsLabel }}
      </span>
      <template #title>
        <span
          v-if="apiKey.allowed_ips?.length"
          class="api-key-item-permissions-summary__tooltip"
        >
          {{ apiKey.allowed_ips.join(', ') }}
        </span>
        <span v-else>Requests accepted from any IP address.</span>
      </template>
    </BittsTooltip>
    <span class="api-key-item-permissions-summary__sep">·</span>
    <span>{{ routesLabel }}</span>
  </span>
</template>

<script setup lang="ts">
import { BittsTooltip } from '@crossbeam/bitts';

import { computed } from 'vue';

import type { ApiKey } from '@/types/api_keys';

const props = defineProps<{ apiKey: ApiKey }>();

function countLabel(
  items: readonly string[] | null | undefined,
  name: string,
  fallback?: string,
): string {
  const count = items?.length ?? 0;
  if (!count) return fallback ?? `0 ${name}s`;
  return `${count} ${name}${count === 1 ? '' : 's'}`;
}

const scopesLabel = computed(() => countLabel(props.apiKey.scopes, 'scope'));
const ipsLabel = computed(() =>
  countLabel(props.apiKey.allowed_ips, 'IP', 'All IPs'),
);
const routesLabel = computed(() =>
  countLabel(props.apiKey.allowed_routes, 'route', 'All routes'),
);
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
.api-key-item-permissions-summary {
  @apply text-sm font-medium text-neutral-text inline-flex items-center gap-6;
}

.api-key-item-permissions-summary__piece {
  @apply underline decoration-dotted decoration-neutral-border underline-offset-4
    hover:decoration-neutral-accent transition-colors;
}

.api-key-item-permissions-summary__sep {
  @apply text-neutral-text-placeholder;
}

.api-key-item-permissions-summary__tooltip {
  @apply font-mono text-sm;
}
</style>
